import { useCallback } from 'react';
import { carsService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { Car } from '../types';

export function useCars(
  options?: ResourceQueryOptions
): ResourceState<Car> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      carsService.getAll(opts) as unknown as Promise<{ data: Car[]; meta?: any }>,
    []
  );
  return useResourceCollection<Car>(fetchFn, options);
}

export function useCar(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<Car> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (carId: number, opts?: ResourceQueryOptions) =>
      carsService.getById(carId, opts) as unknown as Promise<Car>,
    []
  );
  return useResourceById<Car>(fetchFn, numericId, options);
}

export function useCreateCar(
  onSuccess?: (data: Car) => void
): MutationState<Car> {
  const createFn = useCallback(
    (data: any) => carsService.create(data) as unknown as Promise<Car>,
    []
  );
  return useCreateResource<Car>(createFn, onSuccess);
}

export function useUpdateCar(
  onSuccess?: (data: Car) => void
): MutationState<Car> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return carsService.update(numericId, data) as unknown as Promise<Car>;
    },
    []
  );
  return useUpdateResource<Car>(updateFn, onSuccess);
}

/**
 * Delete car using documentId
 * @param data - Full car object containing documentId
 */
export function useDeleteCar(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return carsService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}
// Auto-generated hook file
